/**
 * Client-side tracking utilities for GA4, Meta Pixel and TikTok Pixel
 * Works alongside server-side CAPI for maximum match rates
 */

import { clearOptionalTrackingData, getConsentPreferences, hasConsent } from './consent';
import { normalizeMetaPhone } from './metaPhone';
import './matchingIdentity';
import { recordConsentFunnelAction } from './consentExperiment';
import {
  buildEventContext,
  buildTicketContentData,
  buildViewContentData,
  resolveEventGroupId,
  resolveTicketProductId
} from './metaEventContract';

const GA_SCRIPT_ATTR = 'data-lvm-ga-script';
const APPROVED_ATTRIBUTION_RETENTION_DAYS = 30;
const ATTRIBUTION_STORAGE_KEYS = {
  fbc: 'lvm_tracking_fbc',
  fbp: 'lvm_tracking_fbp',
  ttclid: 'lvm_tracking_ttclid',
  ttp: 'lvm_tracking_ttp',
  externalId: 'lvm_tracking_external_id'
};
let transientTrackingIdentity = null;

// Generate a unique event ID that matches what server sends (for deduplication)
export const generateEventId = (eventType, entityId) => {
  return `${eventType}_${entityId}`;
};

const normalizeTrackingIdPart = (value, fallback = 'unknown') => {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value)
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return normalized || fallback;
};

const buildTrackingEventId = (prefix, parts = []) => (
  [normalizeTrackingIdPart(prefix, 'evt'), ...parts.map((part) => normalizeTrackingIdPart(part))]
    .join('_')
);

export const buildViewContentEventId = (eventId, sessionId) => (
  buildTrackingEventId('vc', [eventId, sessionId])
);

export const buildAddToCartEventId = (eventId, ticketTypeId, sessionId) => (
  buildTrackingEventId('atc', [eventId, ticketTypeId, sessionId])
);

export const parseEmbedAddToCartEventIds = (value, allowedTicketTypeIds = []) => {
  if (typeof value !== 'string' || value.length === 0 || value.length > 8192) return {};

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const allowedIds = new Set(
      allowedTicketTypeIds.map((ticketTypeId) => String(ticketTypeId || '')).filter(Boolean)
    );
    const result = {};

    Object.entries(parsed).slice(0, 100).forEach(([ticketTypeId, eventId]) => {
      const normalizedTicketTypeId = String(ticketTypeId || '');
      const normalizedEventId = String(eventId || '');
      if (!allowedIds.has(normalizedTicketTypeId)) return;
      if (!/^[a-zA-Z0-9_-]{1,255}$/.test(normalizedEventId)) return;
      result[normalizedTicketTypeId] = normalizedEventId;
    });

    return result;
  } catch (error) {
    return {};
  }
};

export const buildInitiateCheckoutEventId = (cart = {}) => (
  buildTrackingEventId('ic', [cart?.cartId || cart?._id || cart?.sessionId])
);

export const buildPurchaseEventId = (order = {}) => (
  buildTrackingEventId('purchase', [order?._id || order?.id || order?.orderNumber])
);

const normalizeIdentityValue = (value, maxLength = 120) => {
  if (value === undefined || value === null) return '';
  return String(value).trim().slice(0, maxLength);
};

const normalizePhoneForTikTok = (value) => {
  const normalized = normalizeIdentityValue(value, 40);
  if (!normalized) return '';
  return normalized.replace(/\D/g, '');
};

const normalizeTextForMeta = (value, maxLength = 120) => (
  normalizeIdentityValue(value, maxLength).toLowerCase()
);

const parseIdentityPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return null;
  const identity = {
    email: normalizeTextForMeta(payload.email || payload.em),
    firstName: normalizeTextForMeta(payload.firstName || payload.fn || payload.firstname),
    lastName: normalizeTextForMeta(payload.lastName || payload.ln || payload.lastname),
    phone: normalizePhoneForTikTok(payload.phone || payload.ph),
    metaPhone: normalizeMetaPhone(payload.phone || payload.ph, payload.phoneCountry),
    externalId: normalizeIdentityValue(payload.externalId || payload.external_id, 255)
  };

  const hasAnyValue = Object.values(identity).some(Boolean);
  return hasAnyValue ? identity : null;
};

const getCookieValue = (cookieName) => {
  if (typeof document === 'undefined') return null;
  const encodedName = `${cookieName}=`;
  const cookies = document.cookie ? document.cookie.split(';') : [];

  for (const segment of cookies) {
    const cookie = segment.trim();
    if (!cookie.startsWith(encodedName)) continue;
    const value = cookie.slice(encodedName.length);
    try {
      return decodeURIComponent(value);
    } catch (error) {
      return value;
    }
  }

  return null;
};

export const getAttributionRetentionSeconds = (env = process.env) => {
  const configuredDays = Number(env.REACT_APP_META_ATTRIBUTION_RETENTION_DAYS || 0);
  if (configuredDays !== APPROVED_ATTRIBUTION_RETENTION_DAYS) {
    return env.NODE_ENV === 'test' ? APPROVED_ATTRIBUTION_RETENTION_DAYS * 24 * 60 * 60 : 0;
  }
  return configuredDays * 24 * 60 * 60;
};

const normalizeMetaCookieIdentifier = (value, now = Date.now()) => {
  const normalized = String(value || '').trim().slice(0, 255);
  const match = normalized.match(/^fb\.\d+\.(\d{10,13})\..+$/);
  const retentionMs = getAttributionRetentionSeconds() * 1000;
  if (!match || retentionMs <= 0) return null;
  const rawTimestamp = Number(match[1]);
  const capturedAt = rawTimestamp < 1e12 ? rawTimestamp * 1000 : rawTimestamp;
  if (!Number.isFinite(capturedAt) || capturedAt > now + (5 * 60 * 1000)) return null;
  if (capturedAt + retentionMs <= now) return null;
  return normalized;
};

const setCookieValue = (cookieName, cookieValue, now = Date.now()) => {
  const maxAge = getAttributionRetentionSeconds();
  if (typeof document === 'undefined' || !cookieValue || maxAge <= 0) return;
  document.cookie = `${cookieName}=${encodeURIComponent(cookieValue)}; max-age=${maxAge}; path=/; SameSite=Lax`;
};

const expireCookieValue = (cookieName) => {
  if (typeof document === 'undefined') return;
  document.cookie = `${cookieName}=; Max-Age=0; path=/; SameSite=Lax`;
};

const getStorageValue = (key, now = Date.now()) => {
  if (typeof window === 'undefined') return null;

  const read = (storage) => {
    const raw = storage?.getItem(key);
    if (!raw) return null;
    try {
      const record = JSON.parse(raw);
      if (record?.version !== 1 || typeof record?.value !== 'string' || !Number.isFinite(Number(record?.expiresAt))) {
        storage?.removeItem(key);
        return null;
      }
      if (Number(record.expiresAt) <= now) {
        storage?.removeItem(key);
        return null;
      }
      return record.value || null;
    } catch (error) {
      // Legacy unbounded values are erased instead of silently extending them.
      storage?.removeItem(key);
      return null;
    }
  };

  try {
    const sessionValue = read(window.sessionStorage);
    if (sessionValue) return sessionValue;
  } catch (error) {
    // ignore storage failures
  }

  try {
    return read(window.localStorage);
  } catch (error) {
    return null;
  }
};

const setStorageValue = (key, value, now = Date.now()) => {
  const retentionSeconds = getAttributionRetentionSeconds();
  if (typeof window === 'undefined' || !value || retentionSeconds <= 0) return;
  const record = JSON.stringify({
    version: 1,
    value,
    capturedAt: now,
    expiresAt: now + (retentionSeconds * 1000)
  });

  try {
    window.sessionStorage.setItem(key, record);
  } catch (error) {
    // ignore storage failures
  }

  try {
    window.localStorage.setItem(key, record);
  } catch (error) {
    // ignore storage failures
  }
};

const buildFbcValue = (fbclid, now = Date.now()) => `fb.1.${now}.${fbclid}`;

/**
 * Capture and persist attribution identifiers early so CAPI can use them later
 * even after route changes or delayed checkout.
 */
export const bootstrapAttributionTracking = (search = '', { now = Date.now() } = {}) => {
  if (typeof window === 'undefined' || !hasConsent('marketing')) {
    // The verified host remains the sole pixel owner. Its identifiers still
    // accompany iframe requests to the server without enabling iframe pixels.
    if (typeof window !== 'undefined') {
      const preferences = getConsentPreferences();
      if (preferences.authority === 'host' && isMatchingIdentityAllowed()) {
        const identifiers = preferences.identifiers || {};
        const click = value => typeof value === 'string' && value.length <= 255 && !/[\s\u0000-\u001f]/.test(value) ? value : null;
        return {fbc:normalizeMetaCookieIdentifier(identifiers.fbc,now),fbp:normalizeMetaCookieIdentifier(identifiers.fbp,now),
          ttclid:click(identifiers.ttclid),ttp:click(identifiers.ttp),externalId:null};
      }
    }
    return { fbc: null, fbp: null, ttclid: null, ttp: null, externalId: null };
  }

  const searchParams = new URLSearchParams(search || window.location.search || '');
  const fbclid = (searchParams.get('fbclid') || '').trim();
  const fbcParam = (searchParams.get('fbc') || searchParams.get('_fbc') || '').trim();
  const fbpParam = (searchParams.get('fbp') || searchParams.get('_fbp') || '').trim();
  const ttclidParam = (searchParams.get('ttclid') || '').trim();
  const ttpParam = (searchParams.get('ttp') || searchParams.get('_ttp') || '').trim();
  let capturedFbc = '';

  if (fbcParam) {
    capturedFbc = normalizeMetaCookieIdentifier(fbcParam, now) || '';
    setCookieValue('_fbc', capturedFbc, now);
    setStorageValue(ATTRIBUTION_STORAGE_KEYS.fbc, capturedFbc, now);
  }
  if (!capturedFbc && fbclid) {
    const generatedFbc = buildFbcValue(fbclid, now);
    capturedFbc = generatedFbc;
    setCookieValue('_fbc', generatedFbc, now);
    setStorageValue(ATTRIBUTION_STORAGE_KEYS.fbc, generatedFbc, now);
  }

  if (fbpParam) {
    const normalizedFbpParam = normalizeMetaCookieIdentifier(fbpParam, now);
    setCookieValue('_fbp', normalizedFbpParam, now);
    setStorageValue(ATTRIBUTION_STORAGE_KEYS.fbp, normalizedFbpParam, now);
  }

  if (ttclidParam) {
    setCookieValue('ttclid', ttclidParam, now);
    setStorageValue(ATTRIBUTION_STORAGE_KEYS.ttclid, ttclidParam, now);
  }

  const fbc = capturedFbc
    || normalizeMetaCookieIdentifier(getCookieValue('_fbc'), now)
    || normalizeMetaCookieIdentifier(getStorageValue(ATTRIBUTION_STORAGE_KEYS.fbc, now), now);

  // _fbp is a Meta browser identifier. Preserve a legitimate cookie/value,
  // but never manufacture one when Meta Pixel has not supplied it.
  const fbp = normalizeMetaCookieIdentifier(fbpParam, now)
    || normalizeMetaCookieIdentifier(getCookieValue('_fbp'), now)
    || normalizeMetaCookieIdentifier(getStorageValue(ATTRIBUTION_STORAGE_KEYS.fbp, now), now);

  const visit = window.LVMVisitAttribution?.get();
  const ttclid = getCookieValue('ttclid') || getStorageValue(ATTRIBUTION_STORAGE_KEYS.ttclid, now)
    || visit?.last_touch?.ttclid || visit?.first_touch?.ttclid || null;

  if (ttpParam) {
    setCookieValue('_ttp', ttpParam, now);
    setStorageValue(ATTRIBUTION_STORAGE_KEYS.ttp, ttpParam, now);
  }

  const ttp = getCookieValue('_ttp') || getStorageValue(ATTRIBUTION_STORAGE_KEYS.ttp, now);

  // external_id is reserved for a lawful, known first-party user key. An
  // anonymous browser/session surrogate is deliberately not created.
  // Previous builds minted an anonymous external_id cookie. Its provenance
  // cannot be distinguished from a lawful known-user key, so M2 erases it.
  if (getCookieValue(ATTRIBUTION_STORAGE_KEYS.externalId)) {
    expireCookieValue(ATTRIBUTION_STORAGE_KEYS.externalId);
  }
  try { window.localStorage.removeItem(ATTRIBUTION_STORAGE_KEYS.externalId); } catch (_) { /* unavailable */ }
  try { window.sessionStorage.removeItem(ATTRIBUTION_STORAGE_KEYS.externalId); } catch (_) { /* unavailable */ }
  const externalId = null;

  return { fbc, fbp, ttclid, ttp, externalId, fbclid: fbclid || null };
};

const isMatchingIdentityAllowed = () => {
  if (typeof window === 'undefined') return false;
  const consent = getConsentPreferences();
  // The verified iframe passes matching to its host/server while the host
  // remains the sole browser pixel owner (browserTracking stays false).
  return consent.marketing === true && (consent.status === 'decided'
    || (consent.status === 'notice' && consent.basis === 'notice_opt_out'));
};

export const getStoredTrackingIdentity = () => {
  if (!isMatchingIdentityAllowed()) return {};
  return {...(transientTrackingIdentity || {}), matchingIdentity: window.LVMMatchingIdentity?.get() || undefined};
};

export const persistTrackingIdentity = (identityPayload = {}) => {
  if (!isMatchingIdentityAllowed()) return null;

  const identity = parseIdentityPayload(identityPayload);
  if (!identity) return null;

  // Raw fields stay transient; only normalized SHA-256 hashes survive a visit.
  transientTrackingIdentity = identity;
  void window.LVMMatchingIdentity?.capture({...identityPayload, metaPhone: identity.metaPhone}).then(record => {
    if (!record || !isMatchingIdentityAllowed()) return;
    refreshMetaAdvancedMatching();
    refreshTikTokAdvancedMatching();
    window.dispatchEvent(new CustomEvent('lvm:matching-identity-updated'));
  }).catch(() => {});

  refreshMetaAdvancedMatching();
  refreshTikTokAdvancedMatching();

  return identity;
};

// ========================================
// GOOGLE ANALYTICS HELPERS
// ========================================

const ensureGtagLoaded = (measurementId) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  const normalizedId = (measurementId || '').trim();
  if (!normalizedId) return false;

  // Load script once
  const existingScript = document.querySelector(`script[${GA_SCRIPT_ATTR}="1"]`);
  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${normalizedId}`;
    script.setAttribute(GA_SCRIPT_ATTR, '1');
    document.head.appendChild(script);
  }

  // Init gtag once
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    const preferences = getConsentPreferences();
    window.gtag('consent', 'default', {
      analytics_storage: preferences.analytics ? 'granted' : 'denied',
      ad_storage: preferences.marketing ? 'granted' : 'denied',
      ad_user_data: preferences.marketing ? 'granted' : 'denied',
      ad_personalization: preferences.marketing ? 'granted' : 'denied'
    });
    window.gtag('js', new Date());
  }

  return true;
};

const ensureGaConfig = (measurementId) => {
  const normalizedId = (measurementId || '').trim();
  if (!normalizedId || !window.gtag) return;

  window._gaConfiguredIds = window._gaConfiguredIds || new Set();
  if (!window._gaConfiguredIds.has(normalizedId)) {
    window.gtag('config', normalizedId, { send_page_view: false });
    window._gaConfiguredIds.add(normalizedId);
  }
};

const getGaSendTo = () => {
  if (typeof window === 'undefined') return [];

  const ids = [];
  if (window._gaGlobalMeasurementId) ids.push(window._gaGlobalMeasurementId);
  if (window._gaActiveMeasurementId) ids.push(window._gaActiveMeasurementId);

  return [...new Set(ids.filter(Boolean))];
};

export const initGoogleAnalytics = (measurementId, { scope = 'active', category = 'analytics' } = {}) => {
  if (!hasConsent(category)) return;
  const normalizedId = (measurementId || '').trim();
  if (!normalizedId) return;
  if (!ensureGtagLoaded(normalizedId)) return;

  ensureGaConfig(normalizedId);

  if (scope === 'global') {
    window._gaGlobalMeasurementId = normalizedId;
  } else if (scope === 'active') {
    window._gaActiveMeasurementId = normalizedId;
  }
};

export const setGlobalGaMeasurementId = (measurementId) => {
  const normalizedId = (measurementId || '').trim();
  if (!normalizedId) {
    window._gaGlobalMeasurementId = '';
    return;
  }
  initGoogleAnalytics(normalizedId, { scope: 'global' });
};

export const setActiveGaMeasurementId = (measurementId) => {
  const normalizedId = (measurementId || '').trim();
  if (!normalizedId) {
    window._gaActiveMeasurementId = '';
    return;
  }
  initGoogleAnalytics(normalizedId, { scope: 'active' });
};

export const trackGaPageView = ({ pagePath, pageTitle, sendTo: explicitSendTo } = {}) => {
  if (!hasConsent('analytics') || !window.gtag) return;
  const sendTo = Array.isArray(explicitSendTo)
    ? explicitSendTo
    : explicitSendTo
      ? [explicitSendTo]
      : getGaSendTo();

  window.gtag('event', 'page_view', {
    page_path: pagePath || window.location.pathname + window.location.search,
    page_title: pageTitle || document.title,
    ...(sendTo.length > 0 ? { send_to: sendTo } : {})
  });
};

export const trackGaEvent = (eventName, params = {}) => {
  if (!hasConsent('analytics') || !window.gtag) return;
  const sendTo = getGaSendTo();

  window.gtag('event', eventName, {
    ...params,
    ...(sendTo.length > 0 ? { send_to: sendTo } : {})
  });
};

// Get Facebook click ID from URL or cookie
export const getFacebookClickId = () => {
  return bootstrapAttributionTracking().fbc;
};

// Get Facebook browser ID from cookie
export const getFacebookBrowserId = () => {
  return bootstrapAttributionTracking().fbp;
};

// Get TikTok click ID from URL
export const getTikTokClickId = () => {
  return bootstrapAttributionTracking().ttclid;
};

export const getTikTokBrowserId = () => {
  return bootstrapAttributionTracking().ttp;
};

export const getTrackingExternalId = () => {
  return getStoredTrackingIdentity().externalId || bootstrapAttributionTracking().externalId;
};

const buildMetaAdvancedMatching = () => {
  const identity = getStoredTrackingIdentity();
  const externalId = getTrackingExternalId();
  const advancedMatching = {...identity.matchingIdentity?.values};

  if (identity.email) advancedMatching.em = identity.email;
  if (identity.metaPhone) advancedMatching.ph = identity.metaPhone;
  if (identity.firstName) advancedMatching.fn = identity.firstName;
  if (identity.lastName) advancedMatching.ln = identity.lastName;
  if (externalId) advancedMatching.external_id = externalId;

  return advancedMatching;
};

export const refreshMetaAdvancedMatching = () => {
  if (!hasConsent('marketing') || !isMetaPixelLoaded()) return;

  const advancedMatching = buildMetaAdvancedMatching();
  if (Object.keys(advancedMatching).length === 0) return;

  const pixelIds = window._fbPixelIds instanceof Set
    ? Array.from(window._fbPixelIds)
    : (window._fbPixelId ? [window._fbPixelId] : []);

  pixelIds.forEach((pixelId) => {
    if (!pixelId) return;
    try {
      window.fbq('init', pixelId, advancedMatching);
    } catch (error) {
      // Advanced matching must never block tracking.
    }
  });
};

const normalizePixelIdList = (pixelIds = []) => {
  if (!Array.isArray(pixelIds)) return [];
  return [...new Set(
    pixelIds
      .map((pixelId) => String(pixelId || '').trim())
      .filter(Boolean)
  )];
};

export const setActiveMetaPixelIds = (pixelIds = null) => {
  if (typeof window === 'undefined') return;
  if (pixelIds === null) {
    delete window._activeMetaPixelIds;
    return;
  }
  window._activeMetaPixelIds = new Set(normalizePixelIdList(pixelIds));
};

export const getActiveMetaPixelIds = () => {
  if (typeof window === 'undefined') return [];
  if (window._activeMetaPixelIds instanceof Set) {
    return Array.from(window._activeMetaPixelIds);
  }
  if (window._globalMetaPixelId) {
    return [window._globalMetaPixelId];
  }
  return [];
};

const buildTikTokAdvancedMatching = () => {
  return window.LVMMatchingIdentity?.getTikTok?.({ttclid:getTikTokClickId(),ttp:getTikTokBrowserId()}) || {};
};

export const refreshTikTokAdvancedMatching = () => {
  if (!hasConsent('marketing') || !isTikTokPixelLoaded()) return;

  const advancedMatching = buildTikTokAdvancedMatching();
  if (Object.keys(advancedMatching).length === 0) return;

  getActiveTikTokPixelIds().forEach(pixelId => {
    try {
      const instance = typeof window.ttq.instance === 'function' ? window.ttq.instance(pixelId) : window.ttq;
      instance.identify?.(advancedMatching);
    } catch (error) { /* Advanced matching must never block tracking. */ }
  });
};

export const setActiveTikTokPixelIds = (pixelIds = null) => {
  if (typeof window === 'undefined') return;
  if (pixelIds === null) {
    delete window._activeTikTokPixelIds;
    return;
  }
  window._activeTikTokPixelIds = new Set(normalizePixelIdList(pixelIds));
};

export const getActiveTikTokPixelIds = () => {
  if (typeof window === 'undefined') return [];
  if (window._activeTikTokPixelIds instanceof Set) {
    return Array.from(window._activeTikTokPixelIds);
  }
  if (window._globalTikTokPixelId) {
    return [window._globalTikTokPixelId];
  }
  return [];
};

// ========================================
// META PIXEL EVENTS
// ========================================

// Check if Meta Pixel is loaded
const isMetaPixelLoaded = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

// Track Meta Pixel event
export const trackMetaEvent = (eventName, params = {}, eventId = null, { custom = false, pixelIds } = {}) => {
  if (!hasConsent('marketing') || !isMetaPixelLoaded()) {
    console.log('Meta Pixel not loaded, skipping client event:', eventName);
    return [];
  }

  try {
    bootstrapAttributionTracking();
    refreshMetaAdvancedMatching();

    const activePixelIds = getActiveMetaPixelIds().filter((id) => !pixelIds || pixelIds.includes(id));
    if (activePixelIds.length === 0) {
      console.log('Meta Pixel has no active target pixels, skipping client event:', eventName);
      return [];
    }

    const handedOff = [];
    activePixelIds.forEach((pixelId) => {
      const deliveryKey = eventId ? `${pixelId}:${eventName}:${eventId}` : '';
      const storageKey = eventName === 'Purchase' && eventId
        ? `lvm_purchase_delivered:${eventId}:meta:${pixelId}`
        : (custom && deliveryKey ? `lvm_meta_custom:${deliveryKey}` : '');
      try {
        const storage = eventName === 'Purchase' ? window.localStorage : window.sessionStorage;
        if (storageKey && storage.getItem(storageKey) === '1') {
          handedOff.push(pixelId);
          return;
        }
      } catch (_error) { /* The shared event ID still deduplicates without storage. */ }
      window._lvmMetaDeliveredEvents = window._lvmMetaDeliveredEvents instanceof Set
        ? window._lvmMetaDeliveredEvents
        : new Set();
      if (deliveryKey && window._lvmMetaDeliveredEvents.has(deliveryKey)) {
        handedOff.push(pixelId);
        return;
      }
      try {
        if (eventId) {
          // Use eventID for deduplication with server-side events.
          window.fbq(custom ? 'trackSingleCustom' : 'trackSingle', pixelId, eventName, params, { eventID: eventId });
        } else {
          window.fbq(custom ? 'trackSingleCustom' : 'trackSingle', pixelId, eventName, params);
        }
        if (deliveryKey) window._lvmMetaDeliveredEvents.add(deliveryKey);
        handedOff.push(pixelId);
        try {
          const storage = eventName === 'Purchase' ? window.localStorage : window.sessionStorage;
          if (storageKey) storage.setItem(storageKey, '1');
        } catch (_error) { /* Tracking never delays payment navigation. */ }
      } catch (_error) { /* A failed destination must not hide the others. */ }
    });
    console.log('📊 Meta Pixel event:', eventName, params, {
      pixels: activePixelIds.length
    });
    return handedOff;
  } catch (error) {
    console.error('Meta Pixel error:', error);
    return [];
  }
};

export const trackCheckoutDetailsCompleted = (signal) => {
  if (!/^checkout_details_[a-f0-9]{24}$/.test(signal?.eventId || '') || !signal?.payload) return;
  if (getConsentPreferences().authority === 'host' && isMatchingIdentityAllowed()) {
    // Existing iframe browser suppression must not lose the server-approved
    // details milestone. The verified widget owns this browser delivery.
    window.dispatchEvent(new CustomEvent('lvm:checkout-details-completed', {detail: signal}));
    return;
  }
  trackMetaEvent('CheckoutDetailsCompleted', signal.payload, signal.eventId, { custom: true });
};

// ========================================
// TIKTOK PIXEL EVENTS
// ========================================

// Check if TikTok Pixel is loaded
const isTikTokPixelLoaded = () => {
  return typeof window !== 'undefined' && typeof window.ttq === 'object' && window.ttq.track;
};

// Track TikTok Pixel event
export const trackTikTokEvent = (eventName, params = {}, eventId = null, { pixelIds } = {}) => {
  if (!hasConsent('marketing') || !isTikTokPixelLoaded()) {
    console.log('TikTok Pixel not loaded, skipping client event:', eventName);
    return [];
  }

  // TikTok event name mapping
  const eventMapping = {
    'ViewContent': 'ViewContent',
    'AddToCart': 'AddToCart',
    'InitiateCheckout': 'InitiateCheckout',
    'Purchase': 'CompletePayment',
    'Search': 'Search'
  };

  const ttEventName = eventMapping[eventName] || eventName;

  try {
    refreshTikTokAdvancedMatching();

    const activePixelIds = getActiveTikTokPixelIds().filter((id) => !pixelIds || pixelIds.includes(id));
    const handedOff = [];
    activePixelIds.forEach((targetId) => {
      const deliveryKey = eventId ? `${targetId}:${ttEventName}:${eventId}` : '';
      const storageKey = eventName === 'Purchase' && eventId
        ? `lvm_purchase_delivered:${eventId}:tiktok:${targetId}` : '';
      try {
        if (storageKey && window.localStorage.getItem(storageKey) === '1') {
          handedOff.push(targetId);
          return;
        }
      } catch (_error) { /* Shared event IDs still deduplicate without storage. */ }
      window._lvmTikTokDeliveredEvents = window._lvmTikTokDeliveredEvents instanceof Set
        ? window._lvmTikTokDeliveredEvents
        : new Set();
      if (deliveryKey && window._lvmTikTokDeliveredEvents.has(deliveryKey)) {
        handedOff.push(targetId);
        return;
      }
      try {
        const target = typeof window.ttq.instance === 'function' ? window.ttq.instance(targetId) : window.ttq;
        if (typeof target?.track !== 'function') return;
        if (eventId) {
          target.track(ttEventName, params, { event_id: eventId });
        } else {
          target.track(ttEventName, params);
        }
        if (deliveryKey) window._lvmTikTokDeliveredEvents.add(deliveryKey);
        handedOff.push(targetId);
        try {
          if (storageKey) window.localStorage.setItem(storageKey, '1');
        } catch (_error) { /* Tracking never delays payment navigation. */ }
      } catch (_error) { /* Retry only the destination whose SDK handoff failed. */ }
    });
    console.log('📊 TikTok Pixel event:', ttEventName, params, {
      pixels: activePixelIds.length || undefined
    });
    return handedOff;
  } catch (error) {
    console.error('TikTok Pixel error:', error);
    return [];
  }
};

export const trackTikTokPageView = () => {
  if (!hasConsent('marketing') || !isTikTokPixelLoaded()) return;

  try {
    refreshTikTokAdvancedMatching();
    const activePixelIds = getActiveTikTokPixelIds();
    const targets = activePixelIds.length && typeof window.ttq.instance === 'function'
      ? activePixelIds.map((pixelId) => window.ttq.instance(pixelId)).filter(Boolean)
      : (activePixelIds.length ? [window.ttq] : []);

    targets.forEach((target) => target.page());
  } catch (error) {
    console.error('TikTok Pixel page view error:', error);
  }
};

// ========================================
// UNIFIED TRACKING FUNCTIONS
// ========================================

export const trackPageView = () => {
  trackMetaEvent('PageView');
  trackTikTokPageView();
};

const getLowestAvailableTicketPrice = (event) => {
  const ticketTypes = Array.isArray(event?.ticketTypes) ? event.ticketTypes : [];
  const now = Date.now();
  const prices = [];

  for (let i = 0; i < ticketTypes.length; i += 1) {
    const ticket = ticketTypes[i];
    if (!ticket || ticket.isHidden || ticket.internalOnly) continue;

    const sold = Number(ticket.sold || 0);
    const quantity = Number(ticket.quantity || 0);
    const price = Number(ticket.price);

    if (!Number.isFinite(price) || quantity <= sold) continue;

    if (ticket.salesStart) {
      const salesStartTime = new Date(ticket.salesStart).getTime();
      if (Number.isFinite(salesStartTime) && now < salesStartTime) continue;
    }

    if (ticket.salesEnd) {
      const salesEndTime = new Date(ticket.salesEnd).getTime();
      if (Number.isFinite(salesEndTime) && now > salesEndTime) continue;
    }

    if (ticket.releaseConditionType === 'after_sold_out') {
      const dependencyIndex = Number.isInteger(ticket.releaseAfterIndex) ? ticket.releaseAfterIndex : null;
      const dependency = dependencyIndex !== null ? ticketTypes[dependencyIndex] : null;

      if (dependency && dependencyIndex < i) {
        const dependencySold = Number(dependency.sold || 0);
        const dependencyQuantity = Number(dependency.quantity || 0);
        if (dependencySold < dependencyQuantity) continue;
      }
    }

    prices.push(price);
  }

  if (!prices.length) return 0;
  return Math.min(...prices);
};

const safeString = (value, fallback = '') => {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
};

const getEventCategory = (event) => {
  if (Array.isArray(event?.categories) && event.categories.length > 0) {
    return safeString(event.categories[0], 'event');
  }
  return safeString(event?.category, 'event');
};

const getEventBrandName = (event) => {
  return safeString(
    event?.brand?.name || event?.organizer?.name || event?.organizerName,
    'LoveVibesMusic'
  );
};

const buildGaItem = ({ event, ticketType, cartItem, contentId, quantity, price }) => {
  const ticketName = safeString(
    ticketType?.name || cartItem?.ticketTypeName || cartItem?.name,
    'Ticket'
  );
  const ticketId = safeString(
    ticketType?._id || ticketType?.id || cartItem?.ticketTypeId || cartItem?.id || contentId,
    contentId
  );
  const safeQuantity = Number.isFinite(Number(quantity)) ? Number(quantity) : 1;
  const safePrice = Number.isFinite(Number(price)) ? Number(price) : 0;

  return {
    item_id: ticketId,
    item_name: `${safeString(event?.title, 'Event')} - ${ticketName}`,
    item_brand: getEventBrandName(event),
    item_category: getEventCategory(event),
    item_variant: ticketName,
    quantity: safeQuantity,
    price: safePrice
  };
};

const buildGaEventItems = (event, fallbackContentId) => {
  const ticketTypes = Array.isArray(event?.ticketTypes) ? event.ticketTypes : [];
  const items = ticketTypes
    .filter((ticketType) => Number(ticketType?.price) >= 0)
    .slice(0, 20)
    .map((ticketType) => buildGaItem({
      event,
      ticketType,
      contentId: `${fallbackContentId}_${safeString(ticketType?._id || ticketType?.name, 'ticket')}`,
      quantity: 1,
      price: ticketType?.price
    }));

  if (items.length > 0) return items;

  return [buildGaItem({
    event,
    contentId: fallbackContentId,
    quantity: 1,
    price: getLowestAvailableTicketPrice(event)
  })];
};

const buildTikTokContents = ({ contentId, contentName, quantity, price }) => {
  const safeContentId = safeString(contentId);
  if (!safeContentId) return [];
  return [{
    content_id: safeContentId,
    content_name: safeString(contentName),
    quantity: Number.isFinite(Number(quantity)) ? Number(quantity) : 1,
    price: Number.isFinite(Number(price)) ? Number(price) : 0
  }];
};

// Track ViewContent (event page view)
export const trackViewContent = (event, eventId = null) => {
  const viewContentEventId = eventId || buildViewContentEventId(event?._id || event?.slug || 'event', getTrackingExternalId());
  const lowestAvailablePrice = getLowestAvailableTicketPrice(event);
  const contentId = resolveEventGroupId(event, safeString(viewContentEventId));
  const params = buildViewContentData({ event, lowestPrice: lowestAvailablePrice });

  trackMetaEvent('ViewContent', params, viewContentEventId);
  trackTikTokEvent('ViewContent', {
    content_type: 'product_group',
    content_id: contentId,
    content_ids: [contentId],
    contents: buildTikTokContents({
      contentId,
      contentName: event?.title,
      quantity: 1,
      price: lowestAvailablePrice
    }),
    content_name: event.title,
    value: lowestAvailablePrice,
    currency: 'GBP'
  }, viewContentEventId);

  trackGaEvent('view_item', {
    currency: 'GBP',
    value: lowestAvailablePrice,
    items: buildGaEventItems(event, contentId)
  });
};

// Track AddToCart
export const trackAddToCart = (event, ticketType, quantity, eventId = null) => {
  const value = ticketType.price * quantity;
  const ticketTypeId = safeString(ticketType?._id || ticketType?.id || ticketType?.name);
  const contentId = resolveTicketProductId(event, ticketType, safeString(eventId, 'event'));
  const addToCartEventId = eventId || buildAddToCartEventId(event?._id || event?.slug || 'event', ticketTypeId || 'ticket', getTrackingExternalId());
  const metaPayload = buildTicketContentData({
    includeEventContext: true,
    event,
    items: [{ ...ticketType, quantity }],
    value,
    currency: 'GBP'
  });

  trackMetaEvent('AddToCart', metaPayload, addToCartEventId);

  trackTikTokEvent('AddToCart', {
    content_type: 'product',
    content_id: contentId,
    content_ids: [contentId],
    contents: buildTikTokContents({
      contentId,
      contentName: `${event?.title || 'Event'} - ${ticketType?.name || 'Ticket'}`,
      quantity,
      price: ticketType?.price
    }),
    content_name: `${event.title} - ${ticketType.name}`,
    value,
    currency: 'GBP',
    quantity
  }, addToCartEventId);

  trackGaEvent('add_to_cart', {
    currency: 'GBP',
    value,
    items: [buildGaItem({
      event,
      ticketType,
      contentId,
      quantity,
      price: ticketType?.price
    })]
  });
};

// Track InitiateCheckout
export const trackInitiateCheckout = (cart, event, eventId = null) => {
  const totalValue = cart.items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  const checkoutEventId = eventId || buildInitiateCheckoutEventId(cart);
  const metaPayload = buildTicketContentData({
    includeEventContext: true,
    event,
    items: cart.items,
    value: totalValue,
    currency: 'GBP'
  });
  const primaryContentId = metaPayload.content_ids[0] || resolveEventGroupId(event, safeString(eventId, 'event'));

  trackMetaEvent('InitiateCheckout', metaPayload, checkoutEventId);

  trackTikTokEvent('InitiateCheckout', {
    content_type: 'product',
    content_id: primaryContentId,
    content_ids: metaPayload.content_ids,
    contents: metaPayload.contents.map((item) => ({
      content_id: item.id,
      content_name: item.content_name,
      quantity: item.quantity,
      price: item.item_price
    })),
    content_name: event.title,
    value: totalValue,
    currency: 'GBP',
    quantity: totalItems
  }, checkoutEventId);

  trackGaEvent('begin_checkout', {
    currency: 'GBP',
    value: totalValue,
    items: (cart.items || []).map((item) => buildGaItem({
      event,
      cartItem: item,
      contentId: resolveTicketProductId(event, item, primaryContentId),
      quantity: item?.quantity,
      price: item?.unitPrice
    }))
  });

  return checkoutEventId;
};

// Completion means handoff to every configured browser SDK, not provider
// acceptance or attribution. Failed targets retain the original shared ID.
const queueBrowserPurchase = (event, eventId, metaPayload, tiktokPayload, onComplete) => {
  window._lvmPendingPurchases = window._lvmPendingPurchases || new Map();
  const pending = window._lvmPendingPurchases;
  const metaIds = normalizePixelIdList([
    event?.brand?.tracking?.metaPixelId,
    ...(event?.tracking?.metaPixels || []).filter((p) => p.enabled).map((p) => p.pixelId)
  ]);
  const tiktokIds = normalizePixelIdList([
    event?.brand?.tracking?.tiktokPixelId,
    event?.tracking?.tiktokPixel?.enabled ? event.tracking.tiktokPixel.pixelId : '',
    ...(event?.tracking?.tiktokPixels || []).filter((p) => p.enabled).map((p) => p.pixelId)
  ]);
  const state = { attempts: 0, callbacks: new Set(onComplete ? [onComplete] : []), timer: null };
  pending.set(eventId, state);
  const attempt = () => {
    state.timer = null;
    if (!hasConsent('marketing')) {
      pending.delete(eventId);
      return;
    }
    const expectedMeta = metaIds.length ? metaIds : getActiveMetaPixelIds();
    const expectedTikTok = tiktokIds.length ? tiktokIds : getActiveTikTokPixelIds();
    const meta = trackMetaEvent('Purchase', metaPayload, eventId, { pixelIds: expectedMeta });
    const tiktok = trackTikTokEvent('Purchase', tiktokPayload, eventId, { pixelIds: expectedTikTok });
    const complete = expectedMeta.length + expectedTikTok.length > 0
      && expectedMeta.every((id) => meta.includes(id))
      && expectedTikTok.every((id) => tiktok.includes(id));
    if (complete) {
      try { window.localStorage.setItem(`lvm_purchase_delivered:${eventId}`, '1'); }
      catch (_error) { /* Per-target memory and provider IDs still guard retries. */ }
      pending.delete(eventId);
      state.callbacks.forEach((callback) => { try { callback(); } catch (_error) { /* UI only. */ } });
    } else if (++state.attempts < 40) {
      state.timer = window.setTimeout(attempt, 500);
    } else {
      // A later payment-return visit can retry remaining targets from storage.
      pending.delete(eventId);
    }
  };
  attempt();
};

// Track Purchase (with deduplication event ID)
export const trackPurchase = (order, event, eventId, { onComplete } = {}) => {
  const authoritativePurchase = order?.tracking?.purchase || null;
  const authoritativePayload = authoritativePurchase?.payload && typeof authoritativePurchase.payload === 'object'
    ? authoritativePurchase.payload
    : null;
  const purchaseEventId = safeString(authoritativePurchase?.eventId || eventId) || buildPurchaseEventId(order);
  const browserDeliveryKey = `lvm_purchase_delivered:${purchaseEventId}`;
  try {
    if (window.localStorage?.getItem(browserDeliveryKey) === '1') {
      onComplete?.();
      return purchaseEventId;
    }
  } catch (error) {
    // Provider event_id remains the final deduplication guard when storage is unavailable.
  }
  const pending = window._lvmPendingPurchases?.get(purchaseEventId);
  if (pending) {
    if (onComplete) pending.callbacks.add(onComplete);
    return purchaseEventId;
  }
  const numItems = order.items?.reduce((sum, item) => sum + item.quantity, 0) || 1;
  const fallbackContentId = resolveEventGroupId(event, safeString(order?.orderNumber || eventId, 'order'));
  const fallbackPayload = buildTicketContentData({
    event,
    items: order.items || [],
    value: order.total,
    currency: order.currency || 'GBP'
  });
  const contentIds = fallbackPayload.content_ids;
  const resolvedContentIds = Array.isArray(authoritativePayload?.content_ids) && authoritativePayload.content_ids.length
    ? authoritativePayload.content_ids
    : (contentIds.length ? contentIds : [fallbackContentId]);
  const primaryContentId = resolvedContentIds[0] || fallbackContentId;

  // Meta Pixel
  const metaPayload = authoritativePayload || {
    ...fallbackPayload,
    content_ids: contentIds.length ? contentIds : [fallbackContentId],
    order_id: order.orderNumber
  };
  const tiktokPayload = {
    content_type: 'product',
    content_id: primaryContentId,
    content_ids: resolvedContentIds,
    contents: authoritativePayload?.contents?.map((item) => ({
      content_id: item.id || item.content_id,
      content_name: item.content_name || '',
      quantity: item.quantity,
      price: item.item_price ?? item.price ?? 0
    })) || (order.items || []).map((item) => {
      const contentId = resolveTicketProductId(event, item, fallbackContentId);
      return {
        content_id: contentId,
        content_name: `${event?.title || 'Event'} - ${safeString(item?.ticketTypeName, 'Ticket')}`,
        quantity: Number.isFinite(Number(item?.quantity)) ? Number(item.quantity) : 1,
        price: Number.isFinite(Number(item?.unitPrice)) ? Number(item.unitPrice) : 0
      };
    }),
    content_name: event?.title || 'Event',
    value: authoritativePayload?.value ?? order.total,
    currency: authoritativePayload?.currency || order.currency || 'GBP',
    quantity: authoritativePayload?.num_items ?? numItems,
    order_id: authoritativePayload?.order_id || order.orderNumber
  };
  if (hasConsent('marketing')) {
    queueBrowserPurchase(event, purchaseEventId, { ...buildEventContext(event), ...metaPayload }, tiktokPayload, onComplete);
  }

  // Google Analytics 4
  if (window.gtag) {
    trackGaEvent('purchase', {
      transaction_id: order.orderNumber || order._id,
      value: order.total,
      currency: order.currency || 'GBP',
      items: order.items?.map(item => ({
        item_id: item.ticketTypeId,
        item_name: `${event?.title || 'Event'} - ${item.ticketTypeName || 'Ticket'}`,
        quantity: item.quantity,
        price: item.unitPrice
      })) || []
    });
    console.log('📊 GA4 purchase tracked:', order.orderNumber);
  }

  // Google Ads Conversion (if conversion label is set)
  if (!hasConsent('marketing')) return purchaseEventId;
  if (window.gtag && window._gadsConversionId && window._gadsConversionLabel) {
    window.gtag('event', 'conversion', {
      send_to: `${window._gadsConversionId}/${window._gadsConversionLabel}`,
      value: order.total,
      currency: order.currency || 'GBP',
      transaction_id: order.orderNumber || order._id
    });
    console.log('📊 Google Ads conversion tracked:', order.orderNumber);
  }
  return purchaseEventId;
};

// Track Search
export const trackSearch = (query, resultCount) => {
  trackMetaEvent('Search', {
    search_string: query,
    content_category: 'events',
    num_results: resultCount
  });

  trackTikTokEvent('Search', {
    query
  });
};

export const trackGenerateLead = ({
  leadType = 'lead',
  method = 'form',
  value = 0,
  currency = 'GBP',
  event = null
} = {}) => {
  trackGaEvent('generate_lead', {
    currency,
    value: Number.isFinite(Number(value)) ? Number(value) : 0,
    lead_type: safeString(leadType, 'lead'),
    method: safeString(method, 'form'),
    ...(event?._id ? { event_id: String(event._id) } : {}),
    ...(event?.title ? { event_name: event.title } : {})
  });
};

export const trackSupportRequest = ({
  requestType = 'support',
  method = 'form',
  source = ''
} = {}) => {
  trackGaEvent('support_request', {
    request_type: safeString(requestType, 'support'),
    method: safeString(method, 'form'),
    ...(source ? { source: safeString(source) } : {})
  });
};

// Track custom event
export const trackCustomEvent = (eventName, params = {}) => {
  if (!hasConsent('marketing')) return;
  if (window.fbq) {
    getActiveMetaPixelIds().forEach((pixelId) => {
      window.fbq('trackSingleCustom', pixelId, eventName, params);
    });
  }
  if (window.ttq) {
    const activePixelIds = getActiveTikTokPixelIds();
    const targets = activePixelIds.length && typeof window.ttq.instance === 'function'
      ? activePixelIds.map((pixelId) => window.ttq.instance(pixelId)).filter(Boolean)
      : (activePixelIds.length ? [window.ttq] : []);
    targets.forEach((target) => target.track(eventName, params));
  }
};

export const applyTrackingConsent = (preferences = getConsentPreferences()) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: preferences.analytics ? 'granted' : 'denied',
      ad_storage: preferences.marketing ? 'granted' : 'denied',
      ad_user_data: preferences.marketing ? 'granted' : 'denied',
      ad_personalization: preferences.marketing ? 'granted' : 'denied'
    });
  }

  if (!preferences.analytics) {
    window._gaGlobalMeasurementId = '';
    window._gaActiveMeasurementId = '';
  }

  if (!preferences.marketing) {
    const hostMatchingAllowed = preferences.authority === 'host' && preferences.browserTracking === false
      && isMatchingIdentityAllowed();
    if (!hostMatchingAllowed) transientTrackingIdentity = null;
    delete window._gadsConversionId;
    delete window._gadsConversionLabel;
    delete window._globalMetaPixelId;
    delete window._globalTikTokPixelId;
    delete window._activeMetaPixelIds;
    delete window._activeTikTokPixelIds;
    // Host ownership suppresses iframe pixels, not its authorised matching data.
    if (!hostMatchingAllowed) clearOptionalTrackingData({ analytics: false, marketing: true });

    document.querySelectorAll(
      'script[src*="connect.facebook.net"], script[src*="analytics.tiktok.com"]'
    ).forEach((node) => node.remove());
    delete window.fbq;
    delete window.ttq;
    delete window._fbq;
    delete window._fbPixelIds;
    delete window._ttPixelIds;
    // Delivery ledgers intentionally survive withdrawal for this page so a
    // later state toggle cannot replay an event that already fired.
  }

  if (!preferences.analytics && !preferences.marketing) {
    document.querySelectorAll(`script[${GA_SCRIPT_ATTR}="1"]`).forEach((node) => node.remove());
    delete window.gtag;
    delete window.dataLayer;
    delete window._gaConfiguredIds;
  }
};

// ========================================
// PIXEL INITIALIZATION HELPERS
// ========================================

// Initialize Meta Pixel (call once with brand's pixel ID)
export const initMetaPixel = (pixelId, { scope = 'active' } = {}) => {
  if (!hasConsent('marketing') || !pixelId || typeof window === 'undefined') return;
  bootstrapAttributionTracking();
  if (scope === 'global') {
    window._globalMetaPixelId = pixelId;
  }

  // Skip if already initialized with same pixel
  if (window.fbq && window._fbPixelIds instanceof Set && window._fbPixelIds.has(pixelId)) {
    refreshMetaAdvancedMatching();
    return;
  }

  // Meta Pixel base code
  !function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;
    if (window.LVMTrackingNotice?.enabled()) t.addEventListener('load', () => {
      if (hasConsent('marketing')) recordConsentFunnelAction('meta_pixel_loaded');
    }, { once: true });
    s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');

  window._fbPixelIds = window._fbPixelIds instanceof Set ? window._fbPixelIds : new Set();
  window.fbq('init', pixelId, buildMetaAdvancedMatching());
  window._fbPixelIds.add(pixelId);
  window.fbq('trackSingle', pixelId, 'PageView');
  if (window.LVMTrackingNotice?.enabled()) {
    // These are delivery diagnostics, never a consent grant or provider receipt.
    recordConsentFunnelAction('meta_pageview_queued');
  }
  window._fbPixelId = pixelId;

  console.log('✅ Meta Pixel initialized:', pixelId.slice(0, 6) + '...');
};

// Initialize TikTok Pixel (call once with brand's pixel ID)
export const initTikTokPixel = (pixelId, { scope = 'active' } = {}) => {
  if (!hasConsent('marketing') || !pixelId || typeof window === 'undefined') return;
  if (scope === 'global') {
    window._globalTikTokPixelId = pixelId;
  }

  window._ttPixelIds = window._ttPixelIds instanceof Set ? window._ttPixelIds : new Set();
  if (window._ttPixelId) {
    window._ttPixelIds.add(window._ttPixelId);
  }

  // Skip if already initialized with same pixel
  if (window.ttq && window._ttPixelIds instanceof Set && window._ttPixelIds.has(pixelId)) {
    refreshTikTokAdvancedMatching();
    return;
  }

  // TikTok Pixel base code
  if (!window.ttq || !window.TiktokAnalyticsObject) {
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=i;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript";o.async=!0;o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
    }(window, document, 'ttq');
  }

  window.ttq.load(pixelId);
  refreshTikTokAdvancedMatching();
  const target = typeof window.ttq.instance === 'function' ? window.ttq.instance(pixelId) : window.ttq;
  target.page();
  window._ttPixelId = pixelId;
  window._ttPixelIds.add(pixelId);

  console.log('✅ TikTok Pixel initialized:', pixelId.slice(0, 6) + '...');
};

export default {
  generateEventId,
  bootstrapAttributionTracking,
  buildViewContentEventId,
  buildAddToCartEventId,
  parseEmbedAddToCartEventIds,
  buildInitiateCheckoutEventId,
  buildPurchaseEventId,
  getStoredTrackingIdentity,
  persistTrackingIdentity,
  getFacebookClickId,
  getFacebookBrowserId,
  getTikTokClickId,
  getTikTokBrowserId,
  getTrackingExternalId,
  setActiveMetaPixelIds,
  setActiveTikTokPixelIds,
  getActiveMetaPixelIds,
  getActiveTikTokPixelIds,
  refreshMetaAdvancedMatching,
  refreshTikTokAdvancedMatching,
  trackMetaEvent,
  trackTikTokEvent,
  trackTikTokPageView,
  trackPageView,
  trackViewContent,
  trackAddToCart,
  trackInitiateCheckout,
  trackPurchase,
  trackSearch,
  trackCustomEvent,
  initMetaPixel,
  initTikTokPixel
};
